import { eachDayOfInterval, addDays, format, parseISO } from "date-fns";
import { List, Record } from "immutable";

import { defaultValues } from "@config/defaultValues";

import { AVAILABLE_CURRENCIES } from "@typings/globals";
import { TYPE_OF_SUBPARTS } from "@typings/priceSubparts";

import { DATE_FORMAT } from "@services/DateFormatter";
import { genericCustomStaticDays } from "@services/StaticTimeGenerators";

import {
  END_OF_TENANCY_EXTRA_SERVICES,
  EndOfTenancyTasks,
  EndOfTenancyAvailabilityRaw,
  EndOfTenancyAvailability,
  Task,
  EndOfTenancyPricingDTO,
  EndOfTenancyPricing,
  WarrantyPriceAndCurrency,
  WarrantyPriceAndCurrencyDTO,
  EndOfTenancyAvailabilityV2,
} from "./endOfTenancy.typings";

// *****************
// ***** TASKS *****
// *****************
export const taskFactory = (input: Task) => Record<Task>({ ...input })();

const {
  endOfTenancy: { tasks },
} = defaultValues;

export const endOfTenancyTasks = Record<EndOfTenancyTasks>({
  [END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING,
    selected:
      tasks[END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS].selected,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.BALCONY]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.BALCONY,
    selected: tasks[END_OF_TENANCY_EXTRA_SERVICES.BALCONY].selected,
  }),
})();

export const endOfTenancyTasksAllSelected = Record<EndOfTenancyTasks>({
  [END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS,
    selected: true,
  }),
  [END_OF_TENANCY_EXTRA_SERVICES.BALCONY]: taskFactory({
    service: END_OF_TENANCY_EXTRA_SERVICES.BALCONY,
    selected: true,
  }),
})();

// ************************
// ***** AVAILABILITY *****
// ************************
export const endOfTenancyAvailabilityInitialState: EndOfTenancyAvailability = {
  defaultSelection: "",
  canSelectPreviousWeek: true,
  canSelectNextWeek: true,
  weekStartDate: "",
  days: List(),
  staticDays: List(),
};
export const endOfTenancyAvailabilityV2InitialState: EndOfTenancyAvailabilityV2 =
  {
    defaultSelection: "",
    canSelectPreviousWeek: true,
    canSelectNextWeek: true,
    weekStartDate: "",
    availableDays: List(),
    staticDays: null,
  };

export const endOfTenancyAvailabilityFactory = (
  input: EndOfTenancyAvailabilityRaw,
): Record<EndOfTenancyAvailability> => {
  // If somehow `input` is `undefined`
  // It's happening for E2E tests
  if (!Object.keys(input).length) {
    return Record<EndOfTenancyAvailability>(
      endOfTenancyAvailabilityInitialState,
    )();
  }

  const staticDays = eachDayOfInterval({
    start: parseISO(input.weekStartDate),
    end: addDays(parseISO(input.weekStartDate), 6),
  }).map(day => ({ day: format(day, DATE_FORMAT.YEAR_MONTH_DAY) }));

  return Record<EndOfTenancyAvailability>(endOfTenancyAvailabilityInitialState)(
    {
      ...input,
      days: List(input.days),
      staticDays: List(staticDays),
    },
  );
};

export const endOfTenancyCustomAvailabilityFactory = (
  { days, weekStartDate, ...input }: EndOfTenancyAvailabilityRaw,
  current: Record<EndOfTenancyAvailabilityV2>,
  chunkSize = 3,
  isExtraRequest?: boolean,
): Record<EndOfTenancyAvailabilityV2> => {
  let staticDays = current.get("staticDays");
  const staticDaysLength = Object.keys(staticDays || {}).length;

  const availableDays = current.get("availableDays").toArray();
  const newAvailableDays = days.map(day => day.day);

  if (
    !staticDays ||
    (!!staticDaysLength && staticDaysLength % chunkSize !== 0)
  ) {
    staticDays = genericCustomStaticDays(chunkSize);
  }

  return Record<EndOfTenancyAvailabilityV2>(current.toObject())({
    ...input,
    weekStartDate: isExtraRequest
      ? current.get("weekStartDate")
      : weekStartDate,
    availableDays: List(
      Array.from(new Set([...availableDays, ...newAvailableDays])),
    ),
    staticDays,
  });
};

// ********************
// ***** PRICING ******
// ********************
export const pricingInitialState: EndOfTenancyPricing = {
  oncePricePerHour: 0,
  displayedPriceForFirstBooking: 0,
  currency: AVAILABLE_CURRENCIES.CHF,
  hasSpecialPricing: false,
  specialPricingTranslatedMessage: "",
  recommendedBasisHours: 0,
  recommendedExtraHours: 0,
  discountsToDisplay: List(),
};

export const pricingFactory = ({
  discountsToDisplay,
  ...input
}: EndOfTenancyPricingDTO) =>
  Record<EndOfTenancyPricing>(pricingInitialState)({
    ...input,
    discountsToDisplay: List(
      (Array.isArray(discountsToDisplay) ? discountsToDisplay : []).map(
        ({ type, ...subpart }) => ({
          ...subpart,
          type: type as TYPE_OF_SUBPARTS,
          currency: input.currency,
        }),
      ) ?? [],
    ),
  });

// ********************
// ***** REGISTER ******
// ********************
export const registerFormInitialState = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  address: "",
  serviceLocationId: 0,
  floorAndDoor: "",
  entryCode: "",
  phoneNumber: "",
  marketingConsent: false,
};

// ********************
// ***** WARANTY ******
// ********************
export const warrantyPriceAndCurrencyFactory = ({
  currency,
  ...data
}: WarrantyPriceAndCurrencyDTO): WarrantyPriceAndCurrency => ({
  ...data,
  currency: currency as AVAILABLE_CURRENCIES,
});
